import { useEffect, useRef, useState } from "react";
import { ChevronDown, User, Building2, Check, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

/** Header "acting as" switcher — personal account vs. each company (Increment 21).
 *  Context is a UI convenience only; ownership remains the security boundary. */
export function ContextSwitcher() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState<{ personal: any[]; companies: any[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Preload contexts so the switcher can decide whether to render (hidden for accounts with no company).
  useEffect(() => { if (user) api.getContexts().then(setCtx).catch(() => setCtx({ personal: [], companies: [] })); }, [user?.id]);

  if (!user) return null;
  const active = user.activeContext || null;
  const activeCompany = ctx?.companies?.find((c) => c.key === active);
  const label = activeCompany ? activeCompany.label : "Personal";

  async function pick(key: string | null) {
    setBusy(key || "personal");
    try { const u = await api.switchContext(key); if (u) setUser(u); setOpen(false); }
    catch { /* ignore */ } finally { setBusy(null); }
  }
  // Prefer worker personal context, else client, else null
  const personalKey = ctx?.personal?.[0]?.key ?? null;

  // Only render once we know the account belongs to at least one company.
  if (!ctx || ctx.companies.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)}
        className={`h-9 px-2.5 rounded-lg flex items-center gap-1.5 max-w-[200px] font-semibold text-sm transition ${activeCompany ? "bg-navy text-white" : "btn-ghost"}`}
        title="Switch context">
        {activeCompany
          ? <span className="w-5 h-5 rounded bg-white/20 text-white grid place-items-center text-[10px] font-bold shrink-0">{activeCompany.initials}</span>
          : <User className="w-4 h-4 shrink-0" />}
        <span className="truncate">{activeCompany ? label : "Personal"}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 ${activeCompany ? "text-white/70" : "text-mut"}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 card p-1.5 shadow-lift z-50">
          {!ctx ? (
            <div className="py-4 text-center text-mut"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : (
            <>
              <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-mut font-semibold">Act as</div>
              <button onClick={() => pick(personalKey)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-navy-50 text-left">
                <User className="w-4 h-4 text-navy" /><span className="flex-1">Personal account</span>
                {!activeCompany && <Check className="w-4 h-4 text-brand-green" />}
                {busy === "personal" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </button>
              {ctx.companies.length > 0 && <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-mut font-semibold mt-1">Companies</div>}
              {ctx.companies.map((c) => (
                <button key={c.key} onClick={() => pick(c.key)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-navy-50 text-left">
                  <span className="w-5 h-5 rounded bg-navy text-white grid place-items-center text-[10px] font-bold shrink-0">{c.initials}</span>
                  <span className="flex-1 min-w-0"><span className="block truncate">{c.label}</span><span className="text-[11px] text-mut capitalize">{c.role}</span></span>
                  {active === c.key && <Check className="w-4 h-4 text-brand-green" />}
                  {busy === c.key && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
